// Pricing v2 (tokeni, wallet, job flow): API shapes and hooks. Customers never see cost, margin or model names.
import { useQuery } from '@tanstack/react-query';
import { api } from './client';
import type { Bi } from './types';

export interface Flags { pricing_v2: boolean; wallet_v2: boolean; script_flow: boolean; iterations: boolean; ui_compact: boolean; expiry_sms?: boolean }

export interface TemplatePricing {
  categoryCode: string; hasFace: boolean; trending: boolean;
  steps: { key: string; kind: 'image' | 'video'; count: number }[];
  qualities: { quality: string; tiers: { code: string; name: Bi }[] }[];
  durations: { values: number[]; default: number } | null;
  options: { quality: string; durationS: number | null; priceTokens: number; priceTzs: number; tiers: string[] }[];
  addons: { code: string; name: Bi; tokensPerUnit: number; unit: string; charsPerUnit: number | null }[];
  scriptVisibility: 'fields' | 'full' | 'hidden';
  fields: { key: string; label: Bi }[];
  tokenValueTzs: number;
  flowOn: boolean;
}

export interface TopupOptions {
  shortfall: number;
  custom: { amountTzs: number; tokens: number; chenji: number; validityDays: number };
  pack: { code: string; name: string; priceTzs: number; tokens: number; validityDays: number; badge: string | null; chenji: number; betterValue: boolean } | null;
}

export interface Quote {
  quote_id: string; expiresIn: number; priceTokens: number; priceTzs: number; durationS: number | null;
  steps: { key: string; kind: string; tier: string; tierName: Bi; units: number; count: number; tokens: number }[];
  addons: { code: string; units: number; tokens: number }[];
  categoryFeeTokens: number;
  freeIterations: Record<string, number>;
  retryPrices: Record<string, number>;
  retryPct: number;
  balance: number | null; shortfall: number | null; topup: TopupOptions | null;
}

export interface JobOption { quality: string; durationS?: number | null; addons?: Record<string, number> }

export interface ScriptFieldView { key: string; type: 'text' | 'textarea'; label: Bi; maxLen: number; required: boolean; kind: 'scene' | 'overlay' | 'narration' | 'brand' | 'text' }
export interface StepView {
  key: string; kind: 'image' | 'video'; status: 'pending' | 'running' | 'done' | 'failed' | 'released'; count: number; iterable: boolean;
  freeAllowed: number; freeUsed: number; retryTokens: number; windowEndsAt: string | null;
  outputs: { index: number; file: string | null; kind: 'image' | 'video'; working: boolean; sandbox: boolean }[];
  attempts: { id: string; index: number; no: number; kind: string; status: string; file: string | null; tokens: number; at: string }[];
}
export interface Job {
  id: string; code: string; state: 'draft' | 'quoted' | 'held' | 'running' | 'awaiting_approval' | 'succeeded' | 'failed' | 'cancelled' | 'abandoned';
  status: string; currentStep: string | null; failure: string | null; createdAt: string; lang: string;
  template: { id: string; kind: 'image' | 'video'; title: Bi; cover: string | null; aspect: string; hasFace: boolean; photos: { min: number; max: number; hint: Bi }; durationOptions: { values: number[]; default: number } | null; qualityOptions: string[]; addonsAllowed: string[] };
  photos: string[]; consent: boolean;
  option: (JobOption & { promo: string | null }) | null;
  priceTokens: number | null; priceTzs: number | null; watermark: boolean;
  script: { visibility: 'fields' | 'full' | 'hidden'; fields: ScriptFieldView[]; values: Record<string, string>; version: number; finalPromptEn?: string | null; narrationBlock: number | null; charsPerBlock: number | null };
  improves: { used: number; free: number; priceTokens: number };
  retryPct: number;
  steps: StepView[];
  media: { sizes: Record<string, Record<string, string>>; caption: string | null; hashtags: string[] };
}

export interface Catalog {
  pricingVersion: number;
  settings: Record<string, number>;
  packs: { code: string; name: string; priceTzs: number; tokens: number; validityDays: number; badge: string | null; audience: string; bonusPct: number }[];
  plans: { code: string; name: string; priceTzsMonth: number; tokensMonth: number; lotValidityDays: number; seats: number; features: string[]; bonusPct: number }[];
  addons: { code: string; name: Bi; tokensPerUnit: number; unit: string; charsPerUnit: number | null }[];
}

export interface Wallet {
  balance: number; held: number; expiringSoon: number; tokenValueTzs: number; features: string[];
  lots: { id: number; source: string; tokensInitial: number; tokensRemaining: number; free: boolean; allowedTiers: string[] | null; expiresAt: string | null; createdAt: string; expiringSoon: boolean }[];
  holds: { jobId: string; tokens: number; createdAt: string }[];
  subscriptions: { id: string; planCode: string; name: string; status: 'active' | 'grace' | 'expired' | 'cancelled'; periodStart: string; periodEnd: string }[];
}
export interface LedgerRow { id: number; type: string; tokens: number; jobId: string | null; reason: string | null; balanceAfter: number; at: string }
export interface PaymentStatus { reference: string; status: 'pending' | 'completed' | 'failed'; failure: string | null; purpose: string; amountTzs: number; tokens: number | null; jobId: string | null; jobState: Job['state'] | null; balance: number; paymentMode: string }

export const v2keys = {
  wallet: ['wallet'] as const,
  ledger: ['wallet-ledger'] as const,
  catalog: ['pricing-catalog'] as const,
  job: (id: string) => ['job', id] as const,
  referral: ['referral'] as const,
};

export const useCatalog = (enabled = true) => useQuery({ queryKey: v2keys.catalog, queryFn: () => api<Catalog>('GET', '/api/pricing/catalog'), enabled, staleTime: 60_000 });
export const useWallet = (enabled = true) => useQuery({ queryKey: v2keys.wallet, queryFn: () => api<Wallet>('GET', '/api/wallet'), enabled, staleTime: 10_000 });
export const useLedger = (enabled = true) => useQuery({ queryKey: v2keys.ledger, queryFn: () => api<{ ledger: LedgerRow[] }>('GET', '/api/wallet/ledger').then((r) => r.ledger), enabled });
export const useReferral = (enabled = true) => useQuery({ queryKey: v2keys.referral, queryFn: () => api<{ code: string; invited: number; rewarded: number; pending: number; rewardTokens: number; minFirstTopupTzs: number }>('GET', '/api/referral'), enabled });

// Polls while something is being made.
export const useJob = (id: string) =>
  useQuery({
    queryKey: v2keys.job(id),
    queryFn: () => api<Job>('GET', `/api/jobs/${id}`),
    refetchInterval: (q) => {
      const j = q.state.data;
      if (!j) return false;
      const busy = j.state === 'running' || j.steps.some((s) => s.outputs.some((o) => o.working));
      return busy ? 2500 : false;
    },
    retry: 1,
  });

export const getQuote = (templateId: string, o: JobOption & { narrationChars?: number }) =>
  api<Quote>('POST', '/api/quotes', { template_id: templateId, quality: o.quality, duration_s: o.durationS ?? undefined, addons: o.addons, narration_chars: o.narrationChars });

export const fmtTzs = (n: number) => Math.round(n).toLocaleString('en-US');
